<template>
  <div class="ai-jobs-table" :data-density="density" :style="{ '--row-height': rowHeight }">
    <!-- The error banner sits above the table: a failed refresh must not wipe rows the user was already reading. -->
    <div v-if="error" class="error-banner" role="alert">
      <v-icon :size="18" class="error-banner__icon">mdi-alert-circle</v-icon>
      <div class="error-banner__body">
        <div class="error-banner__title">Could not load analyses</div>
        <div class="error-banner__text">{{ error }}</div>
      </div>
      <v-btn outlined small class="error-banner__retry" @click="$emit('retry')">Retry</v-btn>
    </div>

    <div v-if="loading && !hasAnyJobs" class="loading-block" aria-busy="true" aria-live="polite">
      <v-progress-circular indeterminate :size="28" :width="3" color="primary" />
      <span>Loading analyses…</span>
    </div>

    <div v-else-if="!hasAnyJobs && !error" class="state-block">
      <div class="state-block__icon"><v-icon :size="30">mdi-robot-outline</v-icon></div>
      <h3>No analyses yet</h3>
      <p>Run one from a scan in the side panel, or start with Run New Analysis.</p>
      <v-btn color="primary" depressed :disabled="!!runDisabledReason" @click="$emit('run')">
        <v-icon left small>mdi-play</v-icon>
        Run New Analysis
      </v-btn>
    </div>

    <div v-else-if="!groups.length && hasAnyJobs" class="state-block">
      <div class="state-block__icon"><v-icon :size="30">mdi-filter-remove-outline</v-icon></div>
      <h3>No analyses match</h3>
      <p>Nothing matches the current search and status filters.</p>
      <v-btn outlined @click="$emit('clear-filters')">Clear filters</v-btn>
    </div>

    <div v-else-if="groups.length" class="table-wrap">
      <v-progress-linear v-if="loading" indeterminate height="2" color="primary" class="table-refresh" />
      <table class="eg jobs">
        <colgroup>
          <col v-for="column in columns" :key="column.key" :class="`c-${column.key}`" />
        </colgroup>
        <thead>
          <tr>
            <th v-for="column in columns" :key="column.key" :class="column.headClass" scope="col">{{ column.label }}</th>
          </tr>
        </thead>
        <tbody>
          <template v-for="group in groups">
            <!-- The group row carries a real Job cell (frozen like data rows) plus a filler spanning the data columns. -->
            <tr
              :key="group.id"
              class="group-row"
              tabindex="0"
              :data-group="group.id"
              :aria-expanded="String(isExpanded(group))"
              @click="toggleGroup(group.id)"
              @keydown.enter.prevent="toggleGroup(group.id)"
              @keydown.space.prevent="toggleGroup(group.id)">
              <td class="col-job">
                <span class="group-head">
                  <v-icon :size="18" class="chev">mdi-chevron-right</v-icon>
                  <span class="group-icon" :class="{ 'group-icon--inspect': group.kind === groupKinds.AVA_INSPECT }">
                    <v-icon :size="15">{{ group.kind === groupKinds.AVA_INSPECT ? 'mdi-target' : 'mdi-folder-outline' }}</v-icon>
                  </span>
                  <span class="group-title" :title="group.title">{{ group.title }}</span>
                  <span v-if="group.subtitle" class="master-tag">{{ group.subtitle }}</span>
                </span>
              </td>
              <td class="grp-fill" :colspan="columns.length - 1">
                <span class="group-count">{{ groupSummary(group) }}</span>
              </td>
            </tr>
            <template v-if="isExpanded(group)">
              <job-row v-for="job in group.children" :key="job.id" :job="job" :cancellation-state="cancelStates[job.id] || null" @open="$emit('open', $event)" @activity="$emit('activity', $event)" @cancel="$emit('cancel', $event)" />
            </template>
          </template>
        </tbody>
      </table>
    </div>
  </div>
</template>

<script>
  import { JOB_GROUP_KIND } from '../../utils/jobTree';
  import { JOB_UI_STATUS } from '../../utils/findingsMetrics';
  import { DENSITIES, isDensity } from './densityPreference';
  import JobRow from './JobRow.vue';

  const COLUMNS = Object.freeze([
    { key: 'job', label: 'Job', headClass: 'col-job' },
    { key: 'scans', label: 'Scans', headClass: '' },
    { key: 'status', label: 'Status', headClass: '' },
    { key: 'findings', label: 'Findings', headClass: 'num' },
    { key: 'started', label: 'Started', headClass: '' },
    { key: 'by', label: 'By', headClass: '' },
    { key: 'actions', label: 'Actions', headClass: 'actions' },
  ]);

  export default {
    name: 'JobsTable',
    components: { JobRow },
    props: {
      groups: { type: Array, default: () => [] },
      loading: { type: Boolean, default: false },
      error: { type: String, default: null },
      /** True when the facility has jobs at all (before filtering): separates "empty" from "no match". */
      hasAnyJobs: { type: Boolean, default: false },
      density: { type: String, default: DENSITIES.DEFAULT, validator: isDensity },
      runDisabledReason: { type: String, default: null },
      cancelStates: { type: Object, default: () => ({}) },
    },
    data() {
      return {
        columns: COLUMNS,
        groupKinds: JOB_GROUP_KIND,
        collapsedIds: [], // group ids the user collapsed; everything else stays expanded across reloads
      };
    },
    computed: {
      rowHeight() {
        return `var(--table-row-${this.density})`;
      },
    },
    methods: {
      isExpanded(group) {
        return !this.collapsedIds.includes(group.id);
      },
      toggleGroup(id) {
        this.collapsedIds = this.collapsedIds.includes(id) ? this.collapsedIds.filter((value) => value !== id) : [...this.collapsedIds, id];
      },
      groupSummary(group) {
        const total = group.children.length;
        const running = group.children.filter((job) => job.uiStatus === JOB_UI_STATUS.RUNNING).length;
        const label = `${total} ${total === 1 ? 'job' : 'jobs'}`;
        return running ? `${label} · ${running} running` : label;
      },
    },
  };
</script>

<style lang="scss">
  @import './aiAnalysisTokens';
  @import './engineeringGrid';

  // Unscoped on purpose: JobRow renders the <tr>/<td> of this table and must share these rules.
  // Every selector is rooted at .ai-jobs-table so nothing leaks. The grid rules live in _engineeringGrid.scss.
  .ai-jobs-table {
    @include ai-analysis-tokens;
    @include engineering-grid;
    --row-height: var(--table-row-default);

    position: relative;
    color: var(--aa-text-primary);
    font-family: var(--ds-font-family-body);

    .table-wrap { max-height: min(70vh, 720px); }
    table.eg.jobs { min-width: 1320px; }
    col.c-job { width: 300px; }
    col.c-scans { width: 200px; }
    col.c-status { width: 310px; } // wide enough for the full stale sentence at the table's minimum width
    col.c-findings { width: 90px; }
    col.c-started { width: 160px; }
    col.c-by { width: 120px; }
    col.c-actions { width: 210px; }

    /* Frozen identity column (Job) with a 2px separator */
    table.eg th.col-job, table.eg td.col-job { position: sticky; left: 0; z-index: var(--z-table-sticky-col); box-shadow: 2px 0 0 var(--aa-border-default); }
    table.eg th.col-job { z-index: var(--z-table-sticky-corner); }
    table.eg tr.data-row td.col-job { padding-left: var(--ds-space-xl); }
    table.eg tr.group-row td.col-job { z-index: var(--z-table-group-frozen); box-shadow: none; }

    .group-icon {
      display: inline-flex;
      flex: 0 0 auto;
      align-items: center;
      justify-content: center;
      width: 26px;
      height: 26px;
      border-radius: var(--ds-radius-sm);
      background: var(--aa-brand-soft);

      .v-icon { color: var(--aa-brand) !important; }
    }
    .group-icon--inspect {
      background: var(--aa-status-running-bg);
      .v-icon { color: var(--aa-status-running) !important; }
    }
    .master-tag {
      display: inline-flex;
      flex: 0 0 auto;
      align-items: center;
      height: 20px;
      padding: 0 8px;
      font-size: var(--aa-grid-font-secondary);
      font-weight: 600;
      letter-spacing: 0.04em;
      color: var(--aa-text-secondary);
      background: var(--aa-surface);
      border: 1px solid var(--aa-border-default);
      border-radius: var(--ds-radius-full);
    }
    &[data-density='compact'] .job-status__chip { height: 20px; }
    &[data-density='compact'] .job-status__stale { margin-top: 0; line-height: 1.15; }

    /* ---------- states ---------- */
    .loading-block {
      display: flex;
      flex-direction: column;
      align-items: center;
      justify-content: center;
      gap: var(--ds-space-sm);
      padding: 48px var(--ds-space-md);
      color: var(--aa-text-secondary);
      font-size: var(--ds-font-body-sm);
    }
    .state-block {
      padding: var(--ds-space-xl);
      text-align: center;

      h3 { font-size: var(--ds-font-subtitle); font-weight: 600; margin: 0 0 var(--ds-space-xs); }
      p { margin: 0 auto var(--ds-space-md); max-width: 460px; font-size: var(--ds-font-body-sm); color: var(--aa-text-muted); line-height: 1.6; }
    }
    .state-block__icon {
      display: inline-flex;
      align-items: center;
      justify-content: center;
      width: 56px;
      height: 56px;
      margin-bottom: var(--ds-space-md);
      border-radius: 50%;
      background: var(--aa-surface-subtle);

      .v-icon { color: var(--aa-text-disabled); }
    }
    .error-banner {
      display: flex;
      align-items: flex-start;
      gap: var(--ds-space-sm);
      margin: 0 0 var(--ds-space-md);
      padding: var(--ds-space-md);
      background: var(--aa-status-failed-bg);
      border: 1px solid rgba(168, 0, 0, 0.25);
      border-left: 3px solid var(--aa-status-failed);
      border-radius: var(--ds-radius-sm);
    }
    .error-banner__icon { color: var(--aa-status-failed) !important; flex: 0 0 auto; margin-top: 1px; }
    .error-banner__body { flex: 1 1 auto; min-width: 0; }
    .error-banner__title { font-weight: 600; font-size: var(--ds-font-body-sm); color: var(--aa-status-failed); }
    .error-banner__text { font-size: var(--ds-font-caption); color: var(--aa-text-secondary); margin-top: 2px; }
    .error-banner__retry { flex: 0 0 auto; }
  }

  .theme--dark .ai-jobs-table {
    @include ai-analysis-tokens-dark;
  }
</style>
